import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Plus } from 'lucide-react';
import { wellbeingService } from '../../../services/wellbeingService';
import { realtimeService } from '../../../services/realtimeService';
import { useAuth } from '../../../contexts/AuthContext';

const WellbeingTracker = () => {
  const { user } = useAuth();
  const [wellbeingData, setWellbeingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntry, setNewEntry] = useState({
    score: 7,
    mood: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadWellbeingData();

    // Subscribe to real-time wellbeing_scores changes for current user
    if (user?.id) {
      const subscription = realtimeService?.subscribeToWellbeingScores((payload) => {
        const { eventType, new: newRecord, old: oldRecord } = payload;

        if (eventType === 'INSERT') {
          // Add new wellbeing entry to the list
          const newEntry = {
            id: newRecord?.id,
            score: newRecord?.score,
            mood: newRecord?.mood,
            notes: newRecord?.notes,
            recordedAt: newRecord?.recorded_at
          };
          setWellbeingData((prev) => [newEntry, ...prev]);
        } else if (eventType === 'UPDATE') {
          // Update existing entry
          setWellbeingData((prev) =>
            prev?.map((entry) =>
              entry?.id === newRecord?.id
                ? {
                    ...entry,
                    score: newRecord?.score,
                    mood: newRecord?.mood,
                    notes: newRecord?.notes
                  }
                : entry
            )
          );
        } else if (eventType === 'DELETE') {
          // Remove deleted entry
          setWellbeingData((prev) => prev?.filter((entry) => entry?.id !== oldRecord?.id));
        }
      }, user?.id);

      // Cleanup subscription on unmount
      return () => {
        subscription?.unsubscribe();
      };
    }
  }, [user?.id]);

  const loadWellbeingData = async () => {
    // Don't load data if user is not authenticated
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const { data, error: fetchError } = await wellbeingService?.getWellbeingHistory(30);
      
      if (fetchError) {
        setError(fetchError?.message || 'Failed to load wellbeing data');
        return;
      }
      
      setWellbeingData(data || []);
    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async () => {
    if (!newEntry?.mood) {
      alert('Please select a mood');
      return;
    }
    
    setSaving(true);
    
    try {
      const { error: saveError } = await wellbeingService?.addWellbeingScore(newEntry);
      
      if (saveError) {
        alert(`Failed to save: ${saveError?.message}`);
        return;
      }
      
      // Reset form and reload data
      setNewEntry({ score: 7, mood: '', notes: '' });
      setShowAddForm(false);
      loadWellbeingData();
    } catch (err) {
      alert(`Failed to save: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const [selectedPeriod, setSelectedPeriod] = useState('week');

  const periodOptions = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'Last 3 Months' }
  ];

  const moodData = [
    { day: 'Mon', mood: 'good', score: 7, note: 'Productive day at work' },
    { day: 'Tue', mood: 'excellent', score: 9, note: 'Great session with Dr. Johnson' },
    { day: 'Wed', mood: 'neutral', score: 5, note: 'Feeling tired' },
    { day: 'Thu', mood: 'good', score: 7, note: 'Better sleep last night' },
    { day: 'Fri', mood: 'excellent', score: 8, note: 'Weekend plans helping motivation' },
    { day: 'Sat', mood: 'good', score: 7, note: 'Relaxing day' },
    { day: 'Sun', mood: 'neutral', score: 6, note: 'Sunday anxiety' }
  ];

  const goals = [
    {
      id: 1,
      title: "Practice daily meditation",
      progress: 75,
      target: "7 days/week",
      current: "5 days this week",
      icon: "Brain"
    },
    {
      id: 2,
      title: "Maintain sleep schedule",
      progress: 85,
      target: "8 hours/night",
      current: "7.5 hours average",
      icon: "Moon"
    },
    {
      id: 3,
      title: "Exercise regularly",
      progress: 60,
      target: "4 times/week",
      current: "3 times this week",
      icon: "Activity"
    }
  ];

  const achievements = [
    {
      id: 1,
      title: "First Session Complete",
      description: "Completed your first mental health session",
      date: "2025-11-25",
      icon: "Award",
      color: "text-warning"
    },
    {
      id: 2,
      title: "Week Streak",
      description: "Logged mood for 7 consecutive days",
      date: "2025-11-30",
      icon: "Flame",
      color: "text-error"
    },
    {
      id: 3,
      title: "Goal Achiever",
      description: "Completed all weekly wellness goals",
      date: "2025-12-01",
      icon: "Target",
      color: "text-success"
    }
  ];

  const getMoodIcon = (mood) => {
    switch (mood) {
      case 'excellent':
        return { name: 'Smile', color: 'text-success' };
      case 'good':
        return { name: 'Meh', color: 'text-primary' };
      case 'neutral':
        return { name: 'Frown', color: 'text-warning' };
      default:
        return { name: 'Meh', color: 'text-muted-foreground' };
    }
  };

  const getMoodHeight = (score) => {
    return `${(score / 10) * 100}%`;
  };

  const handleLogMood = () => {
    console.log('Opening mood logging modal');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-2 text-gray-600">Loading wellbeing data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-8">
          <p className="text-error mb-4">{error}</p>
          <button 
            onClick={loadWellbeingData}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-foreground">Wellbeing Tracker</h2>
        <Select
          options={periodOptions}
          value={selectedPeriod}
          onChange={setSelectedPeriod}
          className="w-40"
        />
      </div>
      {/* Mood Chart */}
      <div className="card-elevated p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Mood Tracking</h3>
          <Button
            variant="outline"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowAddForm(true)}
          >
            Log Today's Mood
          </Button>
        </div>

        <div className="flex items-end justify-between gap-2 h-48 mb-4">
          {moodData?.map((data, index) => {
            const moodIcon = getMoodIcon(data?.mood);
            return (
              <div key={index} className="flex-1 flex flex-col items-center gap-2">
                <div className="relative w-full bg-muted rounded-t-lg overflow-hidden" style={{ height: '100%' }}>
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-primary/20 transition-all duration-300"
                    style={{ height: getMoodHeight(data?.score) }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />
                  </div>
                  <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
                    <Icon name={moodIcon?.name} size={20} className={moodIcon?.color} />
                  </div>
                </div>
                <span className="text-xs font-medium text-muted-foreground">{data?.day}</span>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Icon name="Smile" size={16} className="text-success" />
            <span className="text-muted-foreground">Excellent</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Meh" size={16} className="text-primary" />
            <span className="text-muted-foreground">Good</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Frown" size={16} className="text-warning" />
            <span className="text-muted-foreground">Neutral</span>
          </div>
        </div>
      </div>
      {/* Goals Progress */}
      <div className="card-elevated p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Wellness Goals</h3>
        <div className="space-y-4">
          {goals?.map((goal) => (
            <div key={goal?.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name={goal?.icon} size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">{goal?.title}</h4>
                    <p className="text-xs text-muted-foreground">{goal?.current} of {goal?.target}</p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-primary">{goal?.progress}%</span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-300"
                  style={{ width: `${goal?.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <Button
          variant="ghost"
          iconName="Plus"
          iconPosition="left"
          fullWidth
          className="mt-4"
        >
          Add New Goal
        </Button>
      </div>
      {/* Achievements */}
      <div className="card-elevated p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Recent Achievements</h3>
        <div className="space-y-3">
          {achievements?.map((achievement) => (
            <div key={achievement?.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted hover:bg-muted/70 transition-colors duration-200">
              <div className={`w-10 h-10 rounded-lg bg-background flex items-center justify-center ${achievement?.color}`}>
                <Icon name={achievement?.icon} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-foreground">{achievement?.title}</h4>
                <p className="text-xs text-muted-foreground">{achievement?.description}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Earned on {new Date(achievement.date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WellbeingTracker;